/*
 * The reaction chips under a message (spec 06 §4, A29). One chip per emoji, each a toggle: it says
 * with `aria-pressed` whether the reader is among the people who reacted, and its accessible name
 * says who they are — a tooltip is unreachable by touch and by a keyboard-only pointer, so the
 * names live in the name of the button and the tooltip only repeats them.
 *
 * The row is one tab stop, like the hover bar beside it: a `role="toolbar"` with a roving tabindex,
 * ←/→ between the chips, Home and End at its ends, Enter or Space on the chip the stop is on. A
 * message with seven reactions used to cost seven of the sixteen tab stops of its row, and on a phone
 * seven 44-px chips were seven swipes for a switch-control user (X-B3 P3).
 *
 * The chip never writes the model. `chat.reaction.add` / `.remove` answer with the whole summary
 * and the server publishes `chat.reaction.changed` to the conversation, which the reducer applies —
 * so the reader's own chip and the other browser's chip come from the same event, and a lost frame
 * is repaired by the next one rather than by a delta this file would have to reconcile.
 *
 * `ReactionSummary.userIds` is capped at twenty (protocol.md §2.6): beyond that the name says how
 * many more there are instead of pretending the list is complete.
 */

import { useRef, useState } from "react"
import { useRenderContext } from "../../../core/render/context.ts"
import { Icon } from "../../../core/shell/icons.tsx"
import { client } from "../../../core/store/store.ts"
import { cx } from "../../../lib/cx.ts"
import { Tooltip } from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import type { ReactionSummary } from "../wire/chat.gen.ts"
import { canReact, toggleReaction } from "./deps.ts"
import "./reactions.css"

/**
 * Whether the reader is one of the people behind a chip.
 *
 * The ids decide. `ReactionSummary.me` is a per-viewer fact, and the event that carries one summary
 * to the whole conversation therefore has it false for everybody: only the answer to this reader's
 * own request sets it. Above the cap of twenty the ids are no longer the whole list, so that answer
 * is all there is — and a reader who is neither in the first twenty nor the one who just pressed
 * the chip sees it unpressed. Under-claiming a reaction is the harmless half of being wrong.
 */
function readerReacted(summary: ReactionSummary, meId: number): boolean {
	if (summary.userIds.includes(meId)) return true
	return summary.userIds.length < summary.count && summary.me
}

export interface ReactionBarProps {
	messageId: number
	reactions: readonly ReactionSummary[]
	/**
	 * True while this row is the message list's roving stop. The chips are then in the tab
	 * sequence; on every other row they are reachable but not tabbed through, which is what keeps
	 * the composer a handful of Tab presses away from any row (WCAG 2.4.1).
	 */
	active: boolean
	/**
	 * Opens the emoji picker from the end of the chip row. Adding a second reaction is the most
	 * used action in a chat and P18 left it two steps away inside the menu (spec 06 §4); a surface
	 * that cannot react passes nothing and the chip is absent.
	 */
	onAdd?: (() => void) | undefined
	/** The reader holds no row here: the chips say who reacted, and toggle nothing. */
	readOnly?: boolean | undefined
}

export function ReactionBar({ messageId, reactions, active, onAdd, readOnly }: ReactionBarProps) {
	const { names } = useRenderContext()
	const row = useRef<HTMLDivElement>(null)
	const [stop, setStop] = useState(0)
	if (reactions.length === 0) return null
	const enabled = canReact() && readOnly !== true
	// Read, not subscribed: the signed-in account cannot change under a mounted row, and three
	// hundred rows with a store subscription each is what the render context exists to avoid.
	const meId = client.api.getState().me?.id ?? 0

	// The chips, and the "+" after them where there is one. The stop is clamped rather than reset,
	// because the row it stands in changes under it: somebody taking their reaction back removes a
	// chip, and a stop past the end would leave the row with none at all.
	const count = reactions.length + (onAdd === undefined || !enabled ? 0 : 1)
	const at = Math.min(stop, count - 1)
	const chipStop = (index: number) => (active && index === at ? 0 : -1)
	const moveTo = (index: number) => {
		setStop(index)
		row.current?.querySelectorAll<HTMLElement>("button")[index]?.focus()
	}

	/*
	 * The arrow keys inside the row, in the capture phase and stopped here: the scroller's own key
	 * listener (WindowedList) claims Home and End for the walk over rows and reads `defaultPrevented`
	 * on a native listener that runs before React's bubbled handlers would, exactly as the hover bar
	 * above this row has to deal with (MessageRow).
	 */
	const onKeyDown = (event: React.KeyboardEvent) => {
		let next: number | undefined
		switch (event.key) {
			case "ArrowRight":
				next = (at + 1) % count
				break
			case "ArrowLeft":
				next = (at - 1 + count) % count
				break
			case "Home":
				next = 0
				break
			case "End":
				next = count - 1
				break
			default:
				return
		}
		event.preventDefault()
		event.stopPropagation()
		moveTo(next)
	}

	return (
		<div
			ref={row}
			className="tb-reactions"
			role="toolbar"
			aria-label={t("chat.reaction.group")}
			onKeyDownCapture={onKeyDown}
		>
			{reactions.map((summary, index) => {
				const mine = readerReacted(summary, meId)
				const named = summary.userIds.map((id) => names[id] ?? "").filter((name) => name !== "")
				const rest = summary.count - named.length
				const label =
					named.length === 0
						? t("chat.reaction.countOnly", { emoji: summary.emoji, count: summary.count })
						: rest > 0
							? t("chat.reaction.byAndMore", {
									emoji: summary.emoji,
									names: named.join(", "),
									count: rest,
								})
							: t("chat.reaction.by", { emoji: summary.emoji, names: named.join(", ") })
				return (
					<Tooltip key={summary.emoji} label={label}>
						<button
							type="button"
							className={cx(
								"tb-chip tb-chip--round tb-reaction",
								mine && "tb-chip--mine tb-reaction--mine",
							)}
							aria-pressed={mine}
							aria-label={label}
							disabled={!enabled}
							tabIndex={chipStop(index)}
							onFocus={() => setStop(index)}
							onClick={() => toggleReaction(messageId, summary.emoji, !mine)}
						>
							<span className="tb-reaction__emoji">{summary.emoji}</span>
							<span className="tb-reaction__count">{summary.count}</span>
						</button>
					</Tooltip>
				)
			})}
			{onAdd === undefined || !enabled ? null : (
				<button
					type="button"
					className="tb-chip tb-chip--round tb-reaction tb-reaction--add"
					aria-label={t("chat.reaction.pick")}
					tabIndex={chipStop(reactions.length)}
					onFocus={() => setStop(reactions.length)}
					onClick={onAdd}
				>
					<Icon name="plus" size={16} />
				</button>
			)}
		</div>
	)
}
